import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Home, ArrowLeft, Mail, FileQuestion } from "lucide-react";
import { config } from "@/config/environment";
import { useEffect } from "react";
import * as Sentry from "@sentry/react";

export default function NotFound() {
  const navigate = useNavigate();
  const location = useLocation();
  const attemptedPath = location.pathname;

  // Log 404 errors for monitoring
  useEffect(() => {
    Sentry.captureMessage(`404 Page Not Found: ${attemptedPath}`, 'warning');
    console.warn(`404 - Page not found: ${attemptedPath}`);
  }, [attemptedPath]);

  // Intelligent route suggestions based on attempted URL
  const getSuggestions = () => {
    const path = attemptedPath.toLowerCase();

    if (path.includes('admin')) {
      return [
        { label: 'Admin Dashboard', path: '/admin' },
        { label: 'Platform Admin', path: '/platform-admin' },
        { label: 'Dashboard', path: '/dashboard' },
      ];
    }

    if (path.includes('assess')) {
      return [
        { label: 'Getting Started', path: '/assessment' },
        { label: 'Company Profile', path: '/company' },
        { label: 'Dashboard', path: '/dashboard' },
      ];
    }

    if (path.includes('company') || path.includes('business')) {
      return [
        { label: 'Company Profile', path: '/company' },
        { label: 'Valuations', path: '/valuations' },
        { label: 'Dashboard', path: '/dashboard' },
      ];
    }

    if (path.includes('plan') || path.includes('succession')) {
      return [
        { label: 'Succession Plan', path: '/plan' },
        { label: 'Company Profile', path: '/company' },
        { label: 'Dashboard', path: '/dashboard' },
      ];
    }

    if (path.includes('team') || path.includes('member')) {
      return [
        { label: 'Team Management', path: '/team' },
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Settings', path: '/settings' },
      ];
    }

    if (path.includes('learn') || path.includes('help')) {
      return [
        { label: 'Learning Center', path: '/learn' },
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Getting Started', path: '/assessment' },
      ];
    }

    if (path.includes('profile') || path.includes('setting')) {
      return [
        { label: 'Profile', path: '/settings?tab=profile' },
        { label: 'Settings', path: '/settings' },
        { label: 'Dashboard', path: '/dashboard' },
      ];
    }

    // Default suggestions
    return [
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'Getting Started', path: '/assessment' },
      { label: 'Learning Center', path: '/learn' },
    ];
  };

  const suggestions = getSuggestions();

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <Card className="max-w-md w-full p-8">
        <div className="text-center space-y-6">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
              <FileQuestion className="h-10 w-10 text-primary" />
            </div>
          </div>

          {/* Large 404 */}
          <div>
            <h1 className="text-9xl font-bold text-primary/20 leading-none">404</h1>
            <div className="relative -mt-20">
              <h2 className="text-4xl font-bold text-foreground">
                Page Not Found
              </h2>
            </div>
          </div>

          {/* Friendly message */}
          <p className="text-muted-foreground">
            We couldn't find the page you're looking for. It might have been moved or doesn't exist.
          </p>

          {/* Attempted path */}
          <div className="bg-muted/50 rounded-lg p-3 text-sm">
            <span className="text-muted-foreground">Looking for: </span>
            <code className="text-primary">{attemptedPath}</code>
          </div>

          {/* Primary actions */}
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/dashboard')} className="gap-2">
              <Home className="h-4 w-4" />
              Go Home
            </Button>
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </div>

          {/* Intelligent suggestions */}
          {suggestions.length > 0 && (
            <div className="pt-4">
              <p className="text-sm text-muted-foreground mb-3">
                You might be looking for:
              </p>
              <div className="space-y-2">
                {suggestions.map((suggestion) => (
                  <Button
                    key={suggestion.path}
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={() => navigate(suggestion.path)}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    {suggestion.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Divider */}
          <div className="border-t pt-6">
            {/* Support link */}
            <div className="text-sm text-muted-foreground">
              <p>Need help?</p>
              <a 
                href={`mailto:${config.app.supportEmail}`}
                className="text-primary hover:underline inline-flex items-center gap-1 mt-1"
              >
                <Mail className="h-3 w-3" />
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
